/**
 * Design tokens — single source of truth for colors, spacing, typography,
 * radii, and semantic status/confidence scales across the frontend.
 *
 * Existing surfaces historically consumed colors via CSS variables defined
 * in `index.css`. Those values are mirrored here so that:
 *   1. styled-components can import tokens directly without reading CSS vars.
 *   2. antd's `ConfigProvider` can be seeded from the same palette.
 *   3. New components (StatusChip, ConfidencePill, CategoryTag, etc.) have a
 *      single place to read semantic colors from.
 *
 * When adding a new color or semantic meaning, add it here first, then
 * (optionally) mirror it as a CSS variable in `index.css` if legacy
 * stylesheets need to read it too.
 */

export const brand = {
  primary: "#0C53BB",
  primaryHover: "#0A47A3",
  primaryLight: "#F1F6FE",
};

export const text = {
  primary: "#1B2028",
  secondary: "#48566B",
  tertiary: "#5A6C87",
  muted: "#7688A3",
  disabled: "#98A6BA",
  onAccent: "#FFFFFF",
};

export const surface = {
  page: "#F3F4F7",
  surface: "#FFFFFF",
  hover: "#F5F6F9",
  folderTint: "#F9FAFC",
};

export const border = {
  default: "#DDE2E9",
  light: "#E8ECF1",
  focus: "#0C53BB",
};

/**
 * Semantic status tones. These back the `StatusChip` component and map
 * roughly 1:1 to backend `pipeline_status` values for documents, plus
 * matter-level open/closed state.
 *
 *   ready      → success (green)     → pipeline_status == 'completed'
 *   processing → info (blue)         → pipeline_status in {queued, extracting,
 *                                                           categorizing,
 *                                                           graphing, embedding}
 *   review     → warning (amber)     → pipeline_status == 'review'
 *   failed     → danger  (red)       → pipeline_status == 'failed'
 *   closed     → archive (taupe)     → matter.status == 'closed' — deliberately
 *                                      distinct from `neutral` so a closed
 *                                      matter reads as "archived / finished"
 *                                      rather than "no data".
 */
export const status = {
  ready: {
    fg: "#0F7A3E",
    bg: "#DDF6E9",
    border: "#A8E5C4",
  },
  processing: {
    fg: "#0C53BB",
    bg: "#E6F0FC",
    border: "#B9D2F0",
  },
  review: {
    fg: "#B26B00",
    bg: "#FFF4D9",
    border: "#F2D38A",
  },
  failed: {
    fg: "#A61412",
    bg: "#F6DBDB",
    border: "#EDB1B0",
  },
  closed: {
    fg: "#5C4A3A",
    bg: "#EFE7DA",
    border: "#CDB99F",
  },
  neutral: {
    fg: "#48566B",
    bg: "#EEF1F6",
    border: "#DDE2E9",
  },
};

/**
 * Confidence tone bands. Thresholds are defined by {@link CONFIDENCE_THRESHOLDS}
 * and live in a single place so they're easy to tune once the AI team
 * publishes calibration data.
 */
export const confidence = {
  high: {
    fg: "#0F7A3E",
    bg: "#DDF6E9",
    border: "#A8E5C4",
  },
  medium: {
    fg: "#B26B00",
    bg: "#FFF4D9",
    border: "#F2D38A",
  },
  low: {
    fg: "#A61412",
    bg: "#F6DBDB",
    border: "#EDB1B0",
  },
};

/**
 * Confidence band thresholds (0..1).
 *   >= HIGH  → "high" tone
 *   >= MEDIUM and < HIGH → "medium" tone
 *   < MEDIUM → "low" tone
 */
export const CONFIDENCE_THRESHOLDS = {
  HIGH: 0.85,
  MEDIUM: 0.6,
};

export function getConfidenceTone(value) {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  if (value >= CONFIDENCE_THRESHOLDS.HIGH) return "high";
  if (value >= CONFIDENCE_THRESHOLDS.MEDIUM) return "medium";
  return "low";
}

export const typography = {
  fontFamily: "'Manrope', sans-serif",
  size: {
    xs: "11px",
    sm: "12px",
    base: "13px",
    md: "14px",
    lg: "16px",
    xl: "20px",
    xxl: "24px",
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

export const spacing = {
  0.5: "2px",
  1: "4px",
  1.5: "6px",
  2: "8px",
  2.5: "10px",
  3: "12px",
  3.5: "14px",
  4: "16px",
  5: "20px",
  6: "24px",
  8: "32px",
};

export const radius = {
  sm: "4px",
  md: "6px",
  lg: "8px",
  xl: "12px",
  pill: "9999px",
};

/**
 * antd `ConfigProvider` theme token seed. Keeps antd's primary color,
 * border radius, and font family in sync with the custom palette without
 * overriding every component.
 */
export const antdTheme = {
  token: {
    colorPrimary: brand.primary,
    colorLink: brand.primary,
    colorLinkHover: brand.primaryHover,
    colorBorder: border.default,
    colorBorderSecondary: border.light,
    borderRadius: 6,
    borderRadiusLG: 8,
    fontFamily: typography.fontFamily,
    fontSize: 13,
    colorText: text.primary,
    colorTextSecondary: text.secondary,
  },
};

const tokens = {
  brand,
  text,
  surface,
  border,
  status,
  confidence,
  typography,
  spacing,
  radius,
  antdTheme,
  CONFIDENCE_THRESHOLDS,
  getConfidenceTone,
};

export default tokens;
